import { type ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useModifierConfig,
  ftypeRebuild,
  columnIsDate,
  TypeSimpleOptions,
  TypePeriodOptions,
  TypeTextOptions,
  optionNamespace,
  type DataFrameTypes,
  type Modifier,
  type Value,
  type ModifierGroup,
} from '../../hooks/modifiers';

import IconButton from '../../juristec-ui/core/IconButton';
import InputTextLine from '../../juristec-ui/core/InputTextLine';
import Badge from '../../juristec-ui/core/Badge';
import KpiFilter from '../KpiFilter';
import Tooltip from '../../juristec-ui/core/Tooltip';
import {
  ExpandMore, Edit, Trash, Success, Cancel, SortArrow,
} from '../../juristec-ui/icons';

import { type SelectOption } from '../../types';

import {
  MainContainer,
  HeaderContainer,
  TitleContainer,
  CollapsibleContainer,
  BadgesContainer,
  ControlBtn,
  OrderControls,
  OrderBtn,
} from './styled/KpiFilterApplied.styled';

interface KpiFilterAppliedProps {
  origin: string;
  modifier: Modifier;
  modifiers: ModifierGroup;
  removeFunc: (origin: string, uid: string) => void;
  editFunc: (
    origin: string, uid: string, newValues: Value[], fType: string, newRule?: string,
  ) => void;
  getOptions: (column: string, selector: string) => Promise<SelectOption[]>;
  isLast: boolean;
  position: number;
  changePosition: (origin: string, pos: number, varOp: 1|-1) => void;
  dataFrameTypes: DataFrameTypes;
  nameEdit?: boolean;
  atModal?: boolean;
}

const KpiFilterApplied = ({
  origin,
  modifier,
  modifiers,
  removeFunc,
  editFunc,
  getOptions,
  isLast,
  position,
  changePosition,
  dataFrameTypes,
  nameEdit = false,
  atModal = false,
}: KpiFilterAppliedProps) => {
  const { t } = useTranslation();
  const [extend, setExtend] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);

  const {
    modifierConfig,
    getSimpleOptions,
    handleModifierConfigs,
    fromModifierToConfig,
    //
    checkModifierValues,
  } = useModifierConfig({
    dataFrameTypes,
    // label is not necessary, cant change origin in edit mode
    modifierOrigins: { [origin]: origin },
    getUniqueValues: getOptions,
  });

  useEffect(() => {
    if (editMode) {
      fromModifierToConfig(origin, modifier);
      if (modifier.values.length > 0) setExtend(true);
    }
  }, [editMode]);

  const getLabel = () => ftypeRebuild(
    modifier.ftype || TypeSimpleOptions.enum.VALUES,
    modifier.selector,
    columnIsDate(dataFrameTypes, { value: modifier.column }),
    modifier.inverse,
  ).label;

  const handleModifierName = (e: ChangeEvent<HTMLInputElement>) => {
    handleModifierConfigs({ rule: e.target.value });
  };

  const handleFilterChange = (values: Value[]) => handleModifierConfigs({ values });

  const handleRemove = () => removeFunc(origin, modifier.uid);

  const submitEdit = () => {
    editFunc(
      origin,
      modifier.uid,
      modifierConfig.values,
      modifier.ftype || TypeSimpleOptions.enum.VALUES,
      modifierConfig.rule,
    );
    setEditMode(false);
  };

  return (
    <MainContainer>
      <HeaderContainer>
        {typeof position === 'number' && (
          <OrderControls disableFirst={position === 0} disableLast={isLast}>
            <Tooltip text="Subir prioridade" direction="right">
              <OrderBtn className="order-increase" onClick={() => changePosition(origin, position, -1)}>
                <SortArrow />
              </OrderBtn>
            </Tooltip>
            <Tooltip text="Descer prioridade" direction="right">
              <OrderBtn className="order-decrease" onClick={() => changePosition(origin, position, 1)}>
                <SortArrow />
              </OrderBtn>
            </Tooltip>
          </OrderControls>
        )}
        <TitleContainer>
          {nameEdit && editMode ? (
            <InputTextLine
              value={modifierConfig.rule}
              onChange={handleModifierName}
              style={{ padding: '3px 0' }}
              setValue={() => {}}
            />
          ) : (
            <span className="header-title">
              {typeof position === 'number' ? `${position + 1}°. ` : ''}
              {modifier.rule || modifier.column}
            </span>
          )}
          <span className="header-subtitle">
            {modifier.rule ? `${modifier.column} - ` : ''}
            {t(getLabel(), { ns: optionNamespace })}
          </span>
        </TitleContainer>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {editMode ? (
            <>
              <IconButton
                onClick={submitEdit}
                disabled={!checkModifierValues()}
              >
                <Success />
              </IconButton>
              <IconButton onClick={() => setEditMode(!editMode)}>
                <Cancel />
              </IconButton>
            </>
          ) : (
            <>
              {!Object.keys(TypePeriodOptions.map).includes(modifier.ftype) && (
                <IconButton onClick={() => setEditMode(true)}>
                  <Edit />
                </IconButton>
              )}
            </>
          )}
          <IconButton onClick={handleRemove}>
            <Trash />
          </IconButton>
        </div>
      </HeaderContainer>
      <CollapsibleContainer
        control={extend}
        customSize={(
          [TypeTextOptions.enum.CONTAINS, TypeTextOptions.enum.NOT_CONTAINS] as readonly string[]
        ).includes(modifier.ftype) ? 250 : 0}
      >
        {editMode ? (
          <div style={{ margin: '5px 5px 10px' }}>
            <KpiFilter
              filterType={modifier.ftype || TypeSimpleOptions.enum.VALUES}
              selector={modifier.selector}
              // @ts-expect-error not typed yet
              options={getSimpleOptions(modifiers)}
              // @ts-expect-error not typed yet
              values={modifierConfig.values}
              // @ts-expect-error not typed yet
              handle={handleFilterChange}
              format={dataFrameTypes[modifier.column]}
              atModal={atModal}
            />
          </div>
        ) : (
          <BadgesContainer>
            {modifier.values.map((va) => (
              <Badge
                key={va.toString()}
                color="secondary"
                size="small"
                textTransform="none"
              >
                {va}
              </Badge>
            ))}
          </BadgesContainer>
        )}
      </CollapsibleContainer>
      {!Object.keys(TypePeriodOptions.map).includes(modifier.ftype) && (
        <ControlBtn
          onClick={() => setExtend(!extend)}
          flip={extend}
          lock={editMode}
        >
          <div className="flip-wrapper">
            <ExpandMore />
          </div>
        </ControlBtn>
      )}
    </MainContainer>
  );
};

export default KpiFilterApplied;
